import { Card, CardContent } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Recaptcha from 'react-google-recaptcha';
import styled from 'styled-components';
import cx from 'classnames';
import { signup, SignupOptions } from 'src/services/signup.service';
import { useToast } from 'src/context/ToastContext';
import { joiResolver } from '@hookform/resolvers/joi';
import { signupSchema } from 'src/validation/signup.schema';
import withoutAuth from '../../hoc/withoutAuth';
import AnonymousLayout from '../AnonymousLayout';
import SEO from '../common/SEO';
import { SITE_DESCRIPTION } from '../../config/seo.constants';
import TextField from '../common/TextField';
import Button from '../common/Button';
import regionConfig from '../../config';
import TextLink from '../common/TextLink';

const StyledGuestUserRegistration = styled(AnonymousLayout)`
    #customer-dashboard-appbar {
        position: relative !important;
    }
    .input-box-style {
        display: block;
        width: 100%;
        height: calc(1.5em + 0.75rem + 2px);
        padding: 0.375rem 0.75rem;
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.5;
        color: #495057;
        background-color: #fff;
        background-clip: padding-box;
        border: 1px solid #ced4da;
        border-radius: 0.25rem;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        margin-bottom: 10px;
    }
    .label-text-color {
        color: black;
    }
    .icon-color {
        color: red;
    }
    .hint-text-style-desktop {
        width: 60%;
        height: 100%;
        margin-top: 200px;
        margin-right: 100px;
    }
    .hint-text-style-mobile {
        display: none;
    }
    .content-style {
        display: flex;
        justify-content: center;
        margin-bottom: 140px;
        margin-top: 32px;
        @media (max-width: 480px) {
            height: auto;
        }
    }
    .registration-complete-card {
        margin-top: 64px;
    }
    .signup-form {
        padding-top: 32px;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .register-form-style {
        width: 40%;
        padding: 10px;
        margin-top: 50px;
        margin-bottom: 100px;
    }
    .register-button-enable {
        margin-bottom: 10px;
        border: none;
        outline: none;
        margin-top: 20px;
        background-color: #00aa55;
        width: 100%;
        text-align: center;
        border-radius: 58px;
        line-height: 37px;
        color: white;
    }
    .register-button-disabled {
        background-color: gray;
        pointer-events: none;
        cursor: not-allowed;
    }
    .cancel-button-style {
        color: gray;
        display: flex;
        justify-content: center;
    }
    .terms {
        margin-top: 10px;
        font-size: 16px;
        font-weight: 600;
        color: var(--black-60, rgba(17, 17, 17, 0.6));
        a {
            text-decoration-line: underline;
            text-decoration-style: solid;
        }
    }
    .confirmation-email-heading {
        color: rgb(255, 255, 255);
        font-family: SourceSansPro-Bold;
        font-size: 34px;
        font-weight: bold;
        /* height: 79.56px; */
        letter-spacing: 0.07px;
        line-height: 39.78px;
        text-align: left;
        /* padding-left: 60px; */
    }
    .registered-card {
        width: 500px;
    }
    .heading {
        font-size: 32px;
        font-weight: 700;
        line-height: 40px;
    }
    .instructions {
        font-size: 16px;
        font-weight: 600;
        color: var(--black-60, rgba(17, 17, 17, 0.6));
    }
    @media (max-width: 768px) {
        #customer-dashboard-container {
            display: flex;
            flex-direction: row;
            justify-content: center;
        }
        .content-style {
            width: 100%;
            height: auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
        }
        .hint-text-style-desktop {
            display: none;
        }
        .hint-text-style-mobile {
            display: block;
            margin-left: 0px;
            margin-top: 30px;
            margin-right: 0px;
            padding: 20px;
            width: 100%;
            word-wrap: break-word;
        }
        .register-form-style {
            width: 100%;
            padding: 10px;
            margin-top: 5px;
        }
    }
    .confirmation-signup {
        max-width: 600px;
        width: 100%;
        margin-top: 3rem;
    }
    .captcha {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .optional {
        color: rgba(17, 17, 17, 0.6);
    }
`;

interface SignupFormData {
    email: string;
    firstName: string;
    lastName: string;
    smsPhoneNumber: string;
}
const GuestUserRegistration = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const {
        register,
        handleSubmit,
        errors,
        formState: { isValid },
    } = useForm<SignupFormData>({
        mode: 'onChange',
        defaultValues: {
            email: '',
            firstName: '',
            lastName: '',
            smsPhoneNumber: '',
        },
        resolver: joiResolver(signupSchema),
    });

    const [isRegistered, setRegistered] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [captchaVerified, setCaptchaVerified] = useState(null);
    const captcha = useRef<any>(null);

    // const user = useUser();

    const onSubmit = handleSubmit(async (data) => {
        // productAction('register_customer_request', SnowPlowUtil.getUserInfo(user));

        if (!captchaVerified) {
            toast.error('Please verify that you are a human');

            return;
        }

        setSubmitting(true);

        const options: SignupOptions = {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            smsPhoneNumber: data.smsPhoneNumber,
            roleId: 'ROLE_USER',
            applicationName: 'customer-portal',
            recaptchaToken: captchaVerified,
        };

        const { message, error } = await signup(options);
        if (error) {
            toast.error(error);
        }
        if (message) {
            setRegistered(true);
        }
        captcha.current?.reset();
        setCaptchaVerified(null);
        setSubmitting(false);
    });

    const handleCaptchaVerified = (response: any) => {
        if (response) {
            setCaptchaVerified(response);
        } else {
            setCaptchaVerified(null);
        }
    };

    const recaptchaSiteKey = `${process.env.REACT_APP_RECAPTCH_SITEKEY}`;

    return (
        <StyledGuestUserRegistration isLoginScreen>
            <SEO
                title="Guest User Registration"
                description={`${regionConfig.appName} Guest User Registration - ${SITE_DESCRIPTION}`}
            />

            <div className={`content-style ${isRegistered ? 'registration-complete-card' : ''}`}>
                <Card
                    className={cx('card', {
                        'registered-card': isRegistered,
                    })}
                >
                    <CardContent>
                        {!isRegistered && (
                            <>
                                <div className="heading">Create a free guest account</div>
                                <form onSubmit={onSubmit} className="signup-form">
                                    <TextField
                                        label="First Name"
                                        name="firstName"
                                        inputRef={register}
                                        error={!!errors.firstName}
                                        helperText={errors.firstName?.message}
                                        placeholder="Enter your first name"
                                        aria-required="true"
                                    />
                                    <TextField
                                        label="Last Name"
                                        name="lastName"
                                        inputRef={register}
                                        error={!!errors.lastName}
                                        helperText={errors.lastName?.message}
                                        placeholder="Enter your last name"
                                        aria-required="true"
                                    />
                                    <TextField
                                        label="Email"
                                        name="email"
                                        inputRef={register}
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        placeholder="Enter your email"
                                        aria-required="true"
                                    />
                                    <TextField
                                        label={
                                            <span>
                                                SMS Number&nbsp;
                                                <span className="optional">(optional)</span>
                                            </span>
                                        }
                                        name="smsPhoneNumber"
                                        inputRef={register}
                                        error={!!errors.smsPhoneNumber}
                                        helperText={errors.smsPhoneNumber?.message}
                                        placeholder="Enter your mobile number to receive SMS"
                                        aria-required="true"
                                    />
                                    <div className="captcha">
                                        <Recaptcha
                                            ref={captcha}
                                            sitekey={regionConfig.captchaSiteKey}
                                            render="explicit"
                                            onChange={handleCaptchaVerified}
                                        />
                                    </div>
                                    <Button
                                        id="register-btn"
                                        type="submit"
                                        fullWidth
                                        size="large"
                                        className="primary-bg-color normal-case source-sans-pro-font"
                                        disabled={submitting || !isValid || !captchaVerified}
                                        aria-label="Register"
                                    >
                                        Create account
                                    </Button>

                                    <div className="terms source-sans-pro-font">
                                        <span>Need a paid personal or work account? &nbsp;</span>
                                        <TextLink
                                            href={regionConfig.contactURL}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Contact us
                                        </TextLink>
                                    </div>
                                </form>
                            </>
                        )}
                        {isRegistered && (
                            <div className="w-full flex flex-col">
                                <div className="heading">Confirm your email</div>
                                <div className="instructions">
                                    Please check your inbox and confirm your email before logging in
                                    with your new guest account.
                                </div>
                                <Button
                                    id="go-to-login-btn"
                                    type="button"
                                    size="large"
                                    onClick={() => {
                                        // productAction(
                                        //     'initiate_forgot_password_redirect_to_login',
                                        //     SnowPlowUtil.getUserInfo(user),
                                        // );
                                        navigate('/login', { replace: true });
                                    }}
                                    className="mt-5 source-sans-pro-font primary-bg-color normal-case"
                                    fullWidth
                                    aria-label="Go To Login"
                                >
                                    Go back to Login
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </StyledGuestUserRegistration>
    );
};

export default withoutAuth(GuestUserRegistration);
